import React, { useState, useEffect } from 'react';
import { Clock, Plus, Trash2, Pause, Play } from 'lucide-react';
import Layout from '../components/Layout';
import { PageHeader, Card, Button, Badge, EmptyState, Spinner } from '../components/ui';
import { schedulesApi, platformConnectionsApi } from '../lib/api';
import type { BackupSchedule, PlatformConnection, ResourceTypeConfig } from '../types/vault';

const CRON_PRESETS = [
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Daily at 2am', value: '0 2 * * *' },
  { label: 'Daily at midnight', value: '0 0 * * *' },
  { label: 'Weekly (Sunday midnight)', value: '0 0 * * 0' },
  { label: 'Custom', value: '' },
];

export default function Schedules() {
  const [schedules, setSchedules] = useState<BackupSchedule[]>([]);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [name, setName] = useState('');
  const [selectedConnection, setSelectedConnection] = useState('');
  const [cronPreset, setCronPreset] = useState('0 */6 * * *');
  const [customCron, setCustomCron] = useState('');
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeConfig[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [schedRes, connRes] = await Promise.all([
        schedulesApi.list(),
        platformConnectionsApi.list(),
      ]);
      if (schedRes.success && schedRes.data) setSchedules(schedRes.data.schedules);
      if (connRes.success && connRes.data) setConnections(connRes.data.connections.filter((c) => c.status === 'active'));
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectionChange = async (connId: string) => {
    setSelectedConnection(connId);
    setSelectedTypes([]);
    if (!connId) { setResourceTypes([]); return; }
    try {
      const res = await platformConnectionsApi.getResourceTypes(connId);
      if (res.success && res.data) setResourceTypes(res.data.resourceTypes);
    } catch {
      setResourceTypes([]);
    }
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleCreate = async () => {
    const cron = cronPreset || customCron;
    if (!name || !selectedConnection || !cron || selectedTypes.length === 0) return;

    setCreating(true);
    try {
      const res = await schedulesApi.create({
        connectionId: selectedConnection,
        name,
        cronExpression: cron,
        resourceTypes: selectedTypes,
      });
      if (res.success) {
        setShowCreateModal(false);
        setName('');
        setSelectedConnection('');
        setCronPreset('0 */6 * * *');
        setCustomCron('');
        setSelectedTypes([]);
        loadData();
      } else {
        setError(res.message || 'Failed to create schedule');
      }
    } catch {
      setError('Failed to create schedule');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleEnabled = async (id: string, enabled: boolean) => {
    try {
      await schedulesApi.update(id, { enabled: !enabled });
      loadData();
    } catch {
      setError('Failed to update schedule');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await schedulesApi.delete(id);
      loadData();
    } catch {
      setError('Failed to delete schedule');
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Vault"
          title="Backup Schedules"
          description="Automate your backups with scheduled jobs"
          actions={
            <Button icon={Plus} onClick={() => setShowCreateModal(true)} disabled={connections.length === 0}>
              Create Schedule
            </Button>
          }
        />

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            {error}
            <button onClick={() => setError(null)} className="ml-2 text-danger font-bold">&times;</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : schedules.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="No schedules yet"
            description="Create a schedule to automate your backups."
          />
        ) : (
          <div className="space-y-4">
            {schedules.map((sched) => (
              <Card key={sched.id} padded>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display font-semibold text-ink">{sched.name}</h3>
                      <Badge tone={sched.enabled ? 'success' : 'neutral'} dot pulse={sched.enabled}>
                        {sched.enabled ? 'Active' : 'Paused'}
                      </Badge>
                    </div>
                    <p className="text-sm text-ink-3 mt-1">
                      {sched.connectionName || sched.platform} &middot; <span className="font-mono">{sched.cronExpression}</span>
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {sched.resourceTypes.map((type) => (
                        <span key={type} className="px-2 py-0.5 text-xs bg-surface-2 text-ink-2 rounded-full font-mono">{type}</span>
                      ))}
                    </div>
                    <p className="text-xs text-ink-3 mt-2 font-mono">
                      {sched.lastRunAt ? `Last run: ${new Date(sched.lastRunAt).toLocaleString()}` : 'Never run'}
                      {sched.nextRunAt && ` | Next: ${new Date(sched.nextRunAt).toLocaleString()}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleEnabled(sched.id, sched.enabled)}
                      className="p-2 text-ink-3 hover:text-ink rounded-[10px] hover:bg-surface-2"
                      title={sched.enabled ? 'Pause' : 'Resume'}
                    >
                      {sched.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(sched.id)}
                      className="p-2 text-danger hover:opacity-80 rounded-[10px] hover:bg-danger-soft"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create Schedule Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">Create Backup Schedule</h2>

              <div className="space-y-4">
                <div>
                  <label className="ds-label">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Daily ClickFunnels Backup"
                    className="ds-input"
                  />
                </div>

                <div>
                  <label className="ds-label">Connection</label>
                  <select
                    value={selectedConnection}
                    onChange={(e) => handleConnectionChange(e.target.value)}
                    className="ds-input"
                  >
                    <option value="">Select a connection...</option>
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>{c.displayName} ({c.platform})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="ds-label">Schedule</label>
                  <select
                    value={cronPreset}
                    onChange={(e) => setCronPreset(e.target.value)}
                    className="ds-input"
                  >
                    {CRON_PRESETS.map((p) => (
                      <option key={p.value || 'custom'} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                  {!cronPreset && (
                    <input
                      type="text"
                      value={customCron}
                      onChange={(e) => setCustomCron(e.target.value)}
                      placeholder="0 2 * * *"
                      className="ds-input mt-2 font-mono"
                    />
                  )}
                </div>

                {resourceTypes.length > 0 && (
                  <div>
                    <label className="ds-label">Resource Types</label>
                    <div className="space-y-2">
                      {resourceTypes.map((rt) => (
                        <label key={rt.type} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(rt.type)}
                            onChange={() => toggleType(rt.type)}
                            className="rounded border-line text-accent focus:ring-accent-soft"
                          />
                          <span className="text-sm text-ink-2">{rt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={creating || !name || !selectedConnection || selectedTypes.length === 0}
                >
                  {creating ? 'Creating...' : 'Create Schedule'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
